import useTakeSnapshot from 'pages/Editor/sections/Flow/hooks/useTakeSnapshot';
import { useState, useCallback, useEffect, useRef } from 'react';

import { useReactFlow } from 'reactflow';

import { MemoBlockData, MemoBlockSize } from 'types';

interface UseMemoNodeParams {
  id: string;
  data: MemoBlockData;
}

const useMemoNode = ({ id, data }: UseMemoNodeParams) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [value, setValue] = useState(data.memo);
  const { setNodes } = useReactFlow();

  const { takeSnapshot } = useTakeSnapshot();

  const handleBlur = useCallback(() => {
    setIsEditMode(false);

    if (data.memo === value) return;
    takeSnapshot();

    setNodes((prev) =>
      prev.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: { memo: value },
          };
        }
        return node;
      })
    );
  }, [data, id, value, setNodes, setIsEditMode, takeSnapshot]);

  const handleDoubleClick = useCallback(() => {
    setIsEditMode(true);
  }, [setIsEditMode]);

  const handleChange = useCallback(
    (event: React.FormEvent<HTMLTextAreaElement>) => {
      setValue(event.currentTarget.value);
    },
    [setValue]
  );

  const handleResizeStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const handleResizeEnd = useCallback(
    (_: any, newSize: MemoBlockSize) => {
      const oldSize = data.size;

      if (
        oldSize.width !== newSize.width ||
        oldSize.height !== newSize.height ||
        oldSize.x !== newSize.x ||
        oldSize.y !== newSize.y
      ) {
        setNodes((prev) =>
          prev.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  size: newSize,
                },
              };
            }
            return node;
          })
        );
      }
    },
    [data, id, setNodes]
  );

  useEffect(() => {
    setValue(data.memo);
  }, [data]);

  useEffect(() => {
    if (isEditMode && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = 999999;
    }
  }, [isEditMode]);

  return {
    value,
    isEditMode,
    textareaRef,
    onBlur: handleBlur,
    onDoubleClick: handleDoubleClick,
    onChange: handleChange,
    onResizeStart: handleResizeStart,
    onResizeEnd: handleResizeEnd,
  };
};

export default useMemoNode;
